import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';

import { API_BASE_URL } from '../../config/api';

function FacilityManagement({ onNavigate, onSelectFacility }) {
  const { user } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFacilities();
  }, [filter]);

  const fetchFacilities = async () => {
    setLoading(true);
    try {
      const url = filter
        ? `${API_BASE_URL}/admin/facilities?facility_type=${encodeURIComponent(filter)}`
        : `${API_BASE_URL}/admin/facilities`;
      const token = localStorage.getItem('admin_token');
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setFacilities(await res.json());
    } catch (e) {
      console.error('Failed to fetch facilities', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this facility?")) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/facilities/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchFacilities();
      } else {
        alert("Failed to delete facility");
      }
    } catch (e) {
      console.error('Failed to delete facility', e);
    }
  };

  const getBadgeClass = (type) => {
    switch (type) {
      case 'medical': return 'badge-danger';
      case 'accommodation': return 'badge-info';
      case 'food': return 'badge-success';
      default: return 'badge-info';
    }
  };

  const canCreate = hasPermission(user, 'facility.create');
  const canEdit = hasPermission(user, 'facility.edit');
  const canDelete = hasPermission(user, 'facility.delete');
  const canView = hasPermission(user, 'facility.view');

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <h3>Facility Directory</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
          >
            <option value="">All Types</option>
            <option value="medical">Medical</option>
            <option value="accommodation">Accommodation</option>
            <option value="food">Food Camp</option>
            <option value="others">Others</option>
          </select>
          {canCreate && (
            <button className="btn-primary" onClick={() => onNavigate('facility-create')}>
              + Add Facility
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p>Loading facilities...</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Contact</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {facilities.map((f) => (
              <tr key={f.id}>
                <td>{f.name}</td>
                <td><span className={getBadgeClass(f.facility_type)}>{f.facility_type}</span></td>
                <td>
                  <span className={f.verified ? 'badge-success' : 'badge-info'}>
                    {f.verified ? 'Verified' : 'Unverified'}
                  </span>
                  {f.is_currently_sponsored && (
                    <span className="badge-info" style={{ marginLeft: '4px', backgroundColor: '#ff9800' }}>
                      Sponsored
                    </span>
                  )}
                </td>
                <td>{f.contact_number || 'N/A'}</td>
                <td>
                  {canView && (
                    <button 
                      className="btn-outline btn-small" 
                      style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => {
                        onSelectFacility(f.id);
                        onNavigate('facility-view');
                      }}
                    >
                      View
                    </button>
                  )}
                  {canEdit && (
                    <button 
                      className="btn-outline btn-small" 
                      style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => {
                        onSelectFacility(f.id);
                        onNavigate('facility-edit');
                      }}
                    >
                      Edit
                    </button>
                  )}
                  {canDelete && (
                    <button 
                      className="btn-primary btn-small" 
                      style={{ backgroundColor: '#f44336', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => handleDelete(f.id)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {facilities.length === 0 && (
              <tr><td colSpan="5" style={{ textAlign: 'center' }}>No facilities found.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default FacilityManagement;
